import SeoFooter from "../components/SeoFooter";
import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { 
  Car, Users, Briefcase, Star, Shield, Wifi, Phone, Mail, CheckCircle2, ArrowRight, Luggage
} from "lucide-react";
import ContactPopup from "../components/ContactPopup";
import PageTransition from "../components/PageTransition";

const FleetPage = () => {
  const vehicles = [
    {
      name: "Executive Sedan",
      model: "Lexus ES",
      passengers: 3,
      largeCases: 2,
      carryOns: 2,
      price: "From $90",
      image: "https://static.prod-images.emergentagent.com/jobs/3df45eb8-a170-4bc3-94d8-5c0848dd3464/images/1cc2782b93c210107b385c1a883c8401cca8a6d6c45065eaec21b3fb5ea0c91f.png",
      features: ["Leather interior", "Climate control", "Complimentary water", "Phone chargers"],
      ideal: "Perfect for solo travelers or couples seeking affordable luxury"
    },
    {
      name: "First Class Sedan",
      model: "Mercedes-Benz S-Class",
      passengers: 3,
      largeCases: 2,
      carryOns: 2,
      price: "From $130",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2017.png",
      features: ["Premium leather", "Rear climate zone", "Champagne service available", "Privacy partition"],
      ideal: "Ideal for executives and VIP airport transfers"
    },
    {
      name: "Luxury SUV",
      model: "Audi Q7",
      passengers: 4,
      largeCases: 3,
      carryOns: 2,
      price: "From $105",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/audi_q7-1.png",
      features: ["Spacious interior", "Extra luggage space", "All-wheel drive", "Premium sound system"],
      ideal: "Great for families or groups with extra luggage"
    },
    {
      name: "Luxury People Mover",
      model: "Mercedes V-Class",
      passengers: 6,
      largeCases: 5,
      carryOns: 3,
      price: "From $125",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/mercedes-benz-v-250-1-1.png",
      features: ["Captain chairs", "Ample headroom", "Large cargo area", "USB charging ports"],
      ideal: "Perfect for corporate groups and family airport transfers"
    },
    {
      name: "Mercedes Sprinter 11 Seater",
      model: "Mercedes-Benz Sprinter",
      passengers: 11,
      largeCases: 11,
      carryOns: 6,
      price: "From $165",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png",
      features: ["High roof design", "Air conditioning", "Luggage compartment", "PA system"],
      ideal: "Ideal for medium-sized groups and corporate events"
    },
    {
      name: "Mercedes Sprinter 15 Seater",
      model: "Mercedes-Benz Sprinter XL",
      passengers: 15,
      largeCases: 15,
      carryOns: 8,
      price: "From $220",
      image: "https://www.skytransfers.com.au/wp-content/uploads/2023/11/2018.png",
      features: ["Extended wheelbase", "Premium seating", "Entertainment system", "Climate control"],
      ideal: "Perfect for wedding parties and large group transfers"
    }
  ];

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Limo Near Me Premium Fleet",
    "description": "Luxury vehicle fleet for chauffeur services across Australia including sedans, SUVs, and Mercedes Sprinter vans",
    "numberOfItems": vehicles.length,
    "itemListElement": vehicles.map((vehicle, index) => ({
      "@type": "Product",
      "position": index + 1,
      "name": vehicle.name,
      "description": vehicle.ideal,
      "image": vehicle.image,
      "offers": {
        "@type": "Offer",
        "priceCurrency": "AUD",
        "price": vehicle.price.replace("From $", ""),
        "availability": "https://schema.org/InStock"
      }
    }))
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Our Fleet | Luxury Chauffeur Cars Australia | Limo Near Me</title>
          <meta name="description" content="Browse our premium fleet of luxury vehicles for hire. Mercedes-Benz S-Class, Audi Q7 SUVs, Mercedes V-Class, and Sprinter vans. Professional chauffeur service across Brisbane, Sydney, Melbourne, Gold Coast." />
          <meta name="keywords" content="luxury car hire Australia, Mercedes chauffeur, Audi Q7 hire, private chauffeur vehicles, Limo Near Me fleet, airport transfer vehicles" />
          <link rel="canonical" href="https://www.limonearme.com.au/fleet" />
          <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
        </Helmet>

        <ContactPopup />

        {/* Header */}
        <header className="fixed top-0 left-0 right-0 z-50 bg-white border-b border-black/5">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center">
                <img src="/limo-logo.jpg" alt="Limo Near Me" className="h-10 w-auto" />
              </Link>
              <div className="hidden md:flex items-center gap-6">
                <Link to="/" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Home</Link>
                <Link to="/fleet" className="text-sm text-foreground font-medium">Our Fleet</Link>
                <Link to="/corporate-travel" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Corporate</Link>
                <Link to="/cities" className="text-sm text-muted-foreground hover:text-foreground transition-colors">Cities</Link>
                <a href="tel:+61481437772" className="flex items-center gap-2 text-primary font-semibold">
                  <Phone className="h-4 w-4" />
                  +61 481 437 772
                </a>
                <Link to="/book">
                  <Button size="sm">Book Now</Button>
                </Link>
              </div>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-24 pb-16 bg-gradient-to-b from-gray-900 to-gray-800 text-white">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="text-4xl md:text-5xl font-bold mb-6">
                Our Premium Fleet
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
                From elegant sedans to spacious people movers, our meticulously maintained vehicles 
                ensure you travel in comfort and style across Australia.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                {[
                  { icon: Shield, text: "Fully Licensed & Insured" },
                  { icon: Star, text: "5-Star Rated Service" },
                  { icon: Wifi, text: "Complimentary Wi-Fi" }
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-2 text-gray-300">
                    <item.icon className="h-5 w-5 text-white" />
                    <span>{item.text}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Luggage Guide Banner */}
        <section className="py-6 bg-amber-50 border-y border-amber-200">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
              <Luggage className="h-8 w-8 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">Not Sure Which Vehicle Fits Your Luggage?</p>
                <p className="text-sm text-amber-700">Each vehicle card shows exact capacity: <strong>Passengers</strong> | <strong>Large Cases (23kg)</strong> | <strong>Carry-on Bags</strong></p>
              </div>
            </div>
          </div>
        </section>

        {/* Fleet Grid */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Choose Your Perfect Vehicle</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Select based on your group size and luggage requirements. All vehicles include professional chauffeur service.
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {vehicles.map((vehicle, index) => (
                <motion.div
                  key={vehicle.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl border border-black/5 overflow-hidden hover:shadow-xl transition-shadow"
                >
                  <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-50 flex items-center justify-center p-6">
                    <img 
                      src={vehicle.image} 
                      alt={`${vehicle.name} - Luxury ${vehicle.model} for hire`}
                      className="max-h-full object-contain"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <h2 className="text-xl font-bold">{vehicle.name}</h2>
                      <Link to="/book"><Button size="sm" variant="outline" className="text-xs rounded-full">Get Price</Button></Link>
                    </div>
                    <p className="text-sm text-muted-foreground mb-3">{vehicle.model}</p>
                    
                    {/* Capacity Grid */}
                    <div className="grid grid-cols-3 gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-center">
                        <Users className="h-5 w-5 mx-auto mb-1 text-primary" />
                        <p className="text-lg font-bold">{vehicle.passengers}</p>
                        <p className="text-xs text-muted-foreground">Passengers</p>
                      </div>
                      <div className="text-center">
                        <Luggage className="h-5 w-5 mx-auto mb-1 text-primary" />
                        <p className="text-lg font-bold">{vehicle.largeCases}</p>
                        <p className="text-xs text-muted-foreground">Large Cases</p>
                      </div>
                      <div className="text-center">
                        <Briefcase className="h-5 w-5 mx-auto mb-1 text-primary" />
                        <p className="text-lg font-bold">{vehicle.carryOns}</p>
                        <p className="text-xs text-muted-foreground">Carry-ons</p>
                      </div>
                    </div>
                    
                    <div className="space-y-2 mb-4">
                      {vehicle.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          {feature}
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-muted-foreground italic mb-4">{vehicle.ideal}</p>
                    <Link to="/book">
                      <Button className="w-full">
                        Book This Vehicle
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Vehicle Comparison Table */}
        <section className="py-20 bg-gray-50" data-testid="vehicle-comparison-section">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold mb-4">Vehicle Comparison</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Compare all our vehicles side by side to find the perfect match for your journey.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full bg-white rounded-xl border border-black/5 shadow-sm" data-testid="comparison-table">
                <thead>
                  <tr className="border-b border-black/10 bg-gray-900 text-white">
                    <th className="text-left p-4 font-semibold text-sm rounded-tl-xl">Feature</th>
                    {vehicles.map(v => (
                      <th key={v.name} className="text-center p-4 font-semibold text-sm last:rounded-tr-xl">{v.name}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-black/5">
                  <tr className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-sm text-gray-700">Model</td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-sm">{v.model}</td>)}
                  </tr>
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="p-4 font-medium text-sm text-gray-700">
                      <div className="flex items-center gap-2"><Users className="h-4 w-4 text-primary" /> Passengers</div>
                    </td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-sm font-bold">{v.passengers}</td>)}
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-sm text-gray-700">
                      <div className="flex items-center gap-2"><Luggage className="h-4 w-4 text-primary" /> Large Cases</div>
                    </td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-sm font-bold">{v.largeCases}</td>)}
                  </tr>
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="p-4 font-medium text-sm text-gray-700">
                      <div className="flex items-center gap-2"><Briefcase className="h-4 w-4 text-primary" /> Carry-on Bags</div>
                    </td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-sm font-bold">{v.carryOns}</td>)}
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-sm text-gray-700">Pricing</td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-xs font-semibold text-primary">Instant price at booking</td>)}
                  </tr>
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="p-4 font-medium text-sm text-gray-700">Leather Interior</td>
                    {vehicles.map(v => (
                      <td key={v.name} className="p-4 text-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-gray-50">
                    <td className="p-4 font-medium text-sm text-gray-700">Climate Control</td>
                    {vehicles.map(v => (
                      <td key={v.name} className="p-4 text-center">
                        <CheckCircle2 className="h-5 w-5 text-green-500 mx-auto" />
                      </td>
                    ))}
                  </tr>
                  <tr className="hover:bg-gray-50 bg-gray-50/50">
                    <td className="p-4 font-medium text-sm text-gray-700">Ideal For</td>
                    {vehicles.map(v => <td key={v.name} className="p-4 text-center text-xs text-muted-foreground max-w-[140px]">{v.ideal.replace(/^(Perfect for|Ideal for|Great for) /i, '')}</td>)}
                  </tr>
                  <tr>
                    <td className="p-4"></td>
                    {vehicles.map(v => (
                      <td key={v.name} className="p-4 text-center">
                        <Link to="/book">
                          <Button size="sm" className="rounded-full">
                            Book <ArrowRight className="ml-1 h-3 w-3" />
                          </Button>
                        </Link>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Experience Luxury Travel?</h2>
            <p className="text-primary-foreground/80 mb-8">
              Get an instant quote for your next trip. Professional chauffeurs, premium vehicles, 24/7 service.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book">
                <Button size="lg" variant="secondary" className="rounded-full px-8">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:+61481437772">
                <Button size="lg" variant="outline" className="rounded-full px-8 border-white/30 text-white hover:bg-white/10">
                  <Phone className="h-4 w-4 mr-2" />
                  Call +61 481 437 772
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Footer */}
      <SeoFooter />
      </div>
    </PageTransition>
  );
};

export default FleetPage;
